import React from 'react';
import { humanizeKey } from '../../utils/humanizeKey';
import { hasMultipleOptions, getComponentOptions, getOptionTotal } from '../Quotes/optionUtils';
import {
  MapPin,
  Compass,
  Tent,
  Mountain,
  Calendar,
  Users,
  Footprints,
  Backpack,
  Sunrise,
  TreePine,
  Waves,
  Binoculars,
  CheckCircle2,
  Mail,
  Phone
} from 'lucide-react';
import { TEMPLATE_PALETTES } from './templatePalettes';

// Voyager Pro / Figma reskin - 'expedition' (Expedition / Adventure Trail)
// palette is close in spirit to the previous stone/forest-green look
// already used here - recolored to Figma's exact hex values (forest green
// primary, gold/amber accent - which matches classes already used).
const PALETTE = TEMPLATE_PALETTES.expedition_adventure;

const ExpeditionAdventureTemplate = ({ quote, onPayment, isAgentPreview = false }) => {
  if (!quote) {
    return null;
  }

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getAdventureIcon = (component) => {
    const type = component.type?.toLowerCase() || '';
    const title = component.title?.toLowerCase() || '';
    const style = { color: 'var(--primary)' };

    if (type.includes('hotel') || title.includes('lodge') || title.includes('camp')) {
      return <Tent className="w-6 h-6" style={style} />;
    } else if (type.includes('activity') || title.includes('trek') || title.includes('hike')) {
      return <Mountain className="w-6 h-6" style={style} />;
    } else if (type.includes('safari') || title.includes('wildlife') || title.includes('tour')) {
      return <Binoculars className="w-6 h-6" style={style} />;
    } else if (type.includes('water') || title.includes('river') || title.includes('kayak')) {
      return <Waves className="w-6 h-6" style={style} />;
    } else if (type.includes('park') || title.includes('forest') || title.includes('jungle')) {
      return <TreePine className="w-6 h-6" style={style} />;
    }
    return <MapPin className="w-6 h-6" style={style} />;
  };

  const getClientName = () => {
    if (quote.client?.firstName && quote.client?.lastName) {
      return `${quote.client.firstName} ${quote.client.lastName}`;
    }
    return 'Adventurer';
  };

  return (
    <div className="min-h-screen" style={{ ...PALETTE, backgroundColor: 'var(--background)' }}>
      {/* Header - Trail Guide Style */}
      <div style={{ backgroundColor: 'var(--primary)' }}>
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center gap-3 mb-4">
            <Compass className="w-8 h-8" style={{ color: 'var(--accent)' }} />
            <span className="font-bold uppercase tracking-wider text-sm" style={{ color: 'var(--accent)' }}>Expedition Itinerary</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--background)' }}>
            {quote.quoteTitle || 'Your Adventure Awaits'}
          </h1>

          <div className="flex flex-wrap gap-6" style={{ color: 'var(--background)', opacity: 0.85 }}>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <span>{getClientName()}</span>
            </div>

            {quote.travelDates?.start && (
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                <span>
                  {formatDate(quote.travelDates.start)}
                  {quote.travelDates.end && ` - ${formatDate(quote.travelDates.end)}`}
                </span>
              </div>
            )}

            {(quote.travelers?.adults || quote.travelers?.children?.length) && (
              <div className="flex items-center gap-2">
                <Backpack className="w-5 h-5" />
                <span>
                  {quote.travelers.adults || 0} Explorer{quote.travelers.adults !== 1 ? 's' : ''}
                  {quote.travelers.children?.length > 0 && ` + ${quote.travelers.children.length} Junior${quote.travelers.children.length !== 1 ? 's' : ''}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Trail Guide Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">

        {/* Trail Overview */}
        <div className="mb-12 rounded-lg border-2 border-dashed p-6" style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Mountain className="w-6 h-6" style={{ color: 'var(--primary)' }} />
            <h2 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Trail Map</h2>
          </div>
          <p className="leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
            Follow your expedition route below. Each stop represents a unique experience on your adventure.
          </p>
        </div>

        {/* Trail Stops - Connected with Dashed Lines */}
        {quote.components && quote.components.length > 0 ? (
          <div className="relative">
            {/* Vertical dashed trail line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 border-l-2 border-dashed" style={{ borderColor: 'var(--border)' }}></div>

            {/* Trail Stops */}
            <div className="space-y-8">
              {quote.components.map((component, index) => (
                <div key={component.id || index} className="relative pl-20">
                  {/* Trail Marker */}
                  <div className="absolute left-4 top-8 w-8 h-8 rounded-full border-4 shadow-lg flex items-center justify-center" style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--background)' }}>
                    <span className="text-xs font-bold" style={{ color: 'var(--background)' }}>{index + 1}</span>
                  </div>

                  {/* Stop Card */}
                  <div className="rounded-lg border-2 shadow-sm overflow-hidden hover:shadow-md transition-shadow" style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)' }}>
                    {/* Card Header */}
                    <div className="px-6 py-5 border-b-2 border-dashed" style={{ borderColor: 'var(--border)' }}>
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-start gap-4 min-w-0">
                          <div className="mt-1">
                            {getAdventureIcon(component)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-full" style={{ backgroundColor: 'var(--accent)', color: 'var(--background)' }}>
                                Stop {index + 1}
                              </span>
                            </div>
                            <h3 className="text-xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>
                              {component.title || `Adventure Stop ${index + 1}`}
                            </h3>
                            <div className="text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>
                              {component.type || 'Experience'}
                            </div>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-auto">
                          <div className="text-2xl font-bold" style={{ color: 'var(--primary)' }}>
                            {formatCurrency(component.price)}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Body - Trail Details */}
                    {component.data && Object.keys(component.data).length > 0 && (
                      <div className="px-6 py-5">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.entries(component.data).map(([key, value]) => {
                            if (!value || key === 'isOption' || key === 'optionDescription') return null;

                            const isPriceField = key.toLowerCase().includes('price') ||
                                                 key.toLowerCase().includes('cost') ||
                                                 key.toLowerCase().includes('fee') ||
                                                 key.toLowerCase().includes('total') ||
                                                 key.toLowerCase().includes('deposit');
                            const isNumeric = !isNaN(parseFloat(value)) && isFinite(value);
                            const isLocation = key.toLowerCase().includes('location') ||
                                              key.toLowerCase().includes('destination') ||
                                              key.toLowerCase().includes('place');

                            let displayValue = String(value);
                            if (isPriceField && isNumeric) {
                              displayValue = formatCurrency(parseFloat(value));
                            }

                            return (
                              <div key={key} className="flex items-start gap-2">
                                {isLocation && <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--primary)' }} />}
                                <div className="flex-1">
                                  <div className="text-xs uppercase tracking-wide font-semibold mb-1" style={{ color: 'var(--muted-foreground)' }}>
                                    {humanizeKey(key)}
                                  </div>
                                  <div className="font-medium" style={{ color: 'var(--foreground)' }}>
                                    {displayValue}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Component Photos */}
                    {component.photos && component.photos.length > 0 && (
                      <div className="px-6 pb-5">
                        <h4 className="text-sm font-bold mb-3 uppercase tracking-wide flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
                          <span>📸</span> Photos
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {component.photos.map((photo, photoIndex) => (
                            <div
                              key={photoIndex}
                              className="relative group cursor-pointer rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow border-2"
                              style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background)' }}
                              onClick={() => window.open(photo.url, '_blank')}
                            >
                              <img
                                src={photo.url}
                                alt={photo.name || 'Component photo'}
                                className="w-full h-auto object-contain"
                                style={{ maxHeight: '300px' }}
                              />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                                <span className="text-white text-sm font-bold">{photo.name || 'View Photo'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Component Documents */}
                    {component.documents && component.documents.length > 0 && (
                      <div className="px-6 pb-5">
                        <h4 className="text-sm font-bold mb-3 uppercase tracking-wide flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
                          <span>📋</span> Documents
                        </h4>
                        <div className="space-y-2">
                          {component.documents.map((doc, docIndex) => (
                            <a
                              key={docIndex}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-3 px-4 py-2 rounded-xl transition-colors border-2"
                              style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)' }}
                            >
                              <span className="text-lg">📄</span>
                              <span className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>{doc.name}</span>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {hasMultipleOptions(component) && (
                      <div className="mt-6" style={{ paddingLeft: '1rem', paddingRight: '1rem', paddingBottom: '1rem' }}>
                        <h4 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--foreground)' }}>Choose Your Option</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {getComponentOptions(component).map((option, optIndex) => (
                            <div key={option.id || optIndex} className="rounded-xl overflow-hidden" style={{ border: `2px solid ${optIndex === 0 ? 'var(--accent)' : 'var(--border)'}` }}>
                              {option.photoUrl && (
                                <img src={option.photoUrl} alt={option.label || 'Option'} className="w-full h-28 object-cover" />
                              )}
                              <div className="p-4">
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <span className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{option.label || `Option ${optIndex + 1}`}</span>
                                  {option.badge && (
                                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'var(--muted)', color: 'var(--accent)' }}>{option.badge}</span>
                                  )}
                                </div>
                                {option.description && (
                                  <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>{option.description}</p>
                                )}
                                <p className="text-lg font-bold" style={{ color: 'var(--accent)' }}>{formatCurrency(getOptionTotal(option))}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Trail End Marker */}
            <div className="relative pl-20 mt-8">
              <div className="absolute left-4 top-0 w-8 h-8 rounded-full border-4 shadow-lg flex items-center justify-center" style={{ backgroundColor: 'var(--accent)', borderColor: 'var(--background)' }}>
                <Sunrise className="w-4 h-4 text-white" />
              </div>
              <div className="rounded-lg border-2 border-dashed px-6 py-4" style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--accent)' }}>
                <p className="font-semibold flex items-center gap-2" style={{ color: 'var(--foreground)' }}>
                  <Footprints className="w-5 h-5" />
                  Journey Complete
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border-2 border-dashed p-12 text-center" style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)' }}>
            <Mountain className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--muted-foreground)' }} />
            <p style={{ color: 'var(--muted-foreground)' }}>No trail stops added to this expedition yet</p>
          </div>
        )}

        {/* Field Notes Section */}
        {quote.notes && (
          <div className="mt-12 rounded-lg border-2 p-6" style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-3 mb-4">
              <Compass className="w-6 h-6" style={{ color: 'var(--primary)' }} />
              <h3 className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>Field Notes</h3>
            </div>
            <p className="leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--muted-foreground)' }}>
              {quote.notes}
            </p>
          </div>
        )}

        {/* Expedition Investment Summary */}
        <div className="mt-12 rounded-lg shadow-xl overflow-hidden" style={{ backgroundColor: 'var(--primary)' }}>
          <div className="px-8 py-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
            <h3 className="text-2xl font-bold flex items-center gap-3" style={{ color: 'var(--background)' }}>
              <Backpack className="w-6 h-6" />
              Expedition Investment
            </h3>
          </div>

          <div className="px-8 py-6" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <div className="space-y-6">
              <div className="flex justify-between items-center pb-6" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <span className="text-lg font-medium" style={{ color: 'var(--background)', opacity: 0.85 }}>Total Expedition Cost</span>
                <span className="text-4xl font-bold" style={{ color: 'var(--background)' }}>
                  {formatCurrency(quote.totalPrice)}
                </span>
              </div>

              {quote.depositAmount > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="font-medium" style={{ color: 'var(--background)', opacity: 0.85 }}>Initial Deposit Required</span>
                    <span className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>
                      {formatCurrency(quote.depositAmount)}
                    </span>
                  </div>

                  {quote.depositDueDate && (
                    <div className="flex justify-between items-center text-sm">
                      <span style={{ color: 'var(--background)', opacity: 0.7 }}>Deposit Due Date</span>
                      <span className="font-semibold" style={{ color: 'var(--background)', opacity: 0.85 }}>
                        {formatDate(quote.depositDueDate)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Payment Button */}
            {onPayment && (
              <div className="mt-8">
                <button
                  onClick={onPayment}
                  disabled={isAgentPreview}
                  className={`w-full font-bold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-3 ${
                    isAgentPreview ? 'opacity-50 cursor-not-allowed' : 'shadow-lg hover:shadow-xl hover:brightness-110'
                  }`}
                  style={{ backgroundColor: 'var(--accent)', color: 'var(--primary)' }}
                >
                  <CheckCircle2 className="w-5 h-5" />
                  {isAgentPreview ? 'Preview Mode' : 'Begin Your Expedition'}
                </button>
                {!isAgentPreview && (
                  <p className="text-center text-xs mt-4" style={{ color: 'var(--background)', opacity: 0.7 }}>
                    Secure booking • Adventure insurance available
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Agent Contact Footer */}
        {(quote.agent_name || quote.agent_email || quote.agent_phone) && (
          <div className="mt-12 rounded-2xl border-2 overflow-hidden shadow-lg" style={{ backgroundColor: 'var(--muted)', borderColor: 'var(--border)' }}>
            <div className="px-6 py-4" style={{ backgroundColor: 'var(--foreground)' }}>
              <div className="flex items-center justify-center gap-2" style={{ color: 'var(--accent)' }}>
                <Mountain className="w-6 h-6" />
                <h3 className="text-lg font-bold">Your Expedition Guide</h3>
              </div>
            </div>

            <div className="p-6">
              {quote.agent_name && (
                <div className="text-center mb-4">
                  <p className="text-xl font-bold" style={{ color: 'var(--foreground)' }}>{quote.agent_name}</p>
                  {quote.agent_business_name && (
                    <p className="font-semibold" style={{ color: 'var(--muted-foreground)' }}>{quote.agent_business_name}</p>
                  )}
                </div>
              )}

              <div className="space-y-3 max-w-md mx-auto">
                {quote.agent_email && (
                  <a
                    href={`mailto:${quote.agent_email}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors border-2"
                    style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)' }}
                  >
                    <Mail className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                    <span className="font-medium" style={{ color: 'var(--foreground)' }}>{quote.agent_email}</span>
                  </a>
                )}

                {quote.agent_phone && (
                  <a
                    href={`tel:${quote.agent_phone}`}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl transition-colors border-2"
                    style={{ backgroundColor: 'var(--background)', borderColor: 'var(--border)' }}
                  >
                    <Phone className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                    <span className="font-medium" style={{ color: 'var(--foreground)' }}>{quote.agent_phone}</span>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>
            <Mountain className="w-4 h-4" />
            <span>Expedition Travel Specialists • Est. Since Day One</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpeditionAdventureTemplate;
